<template>
  <div :class="$style.wrapper">
    <button
      type="button"
      :title="$t(`formulate:button.decrement`)"
      :disabled="context.disabled"
      :class="$style.stepper"
      @click="onStep('decrement')"
    >
      -
    </button>
    <input
      v-bind="context.attrs"
      :id="context.id"
      ref="inputRef"
      v-model="value"
      :min="0"
      type="number"
      :name="context.node.name"
      :disabled="context.disabled"
      :class="[context.classes.input, $style.input]"
      @blur="context.handlers.blur"
    />
    <button
      type="button"
      :title="$t(`formulate:button.increment`)"
      :disabled="context.disabled"
      :class="$style.stepper"
      @click="onStep('increment')"
    >
      +
    </button>
  </div>
</template>
<script setup lang="ts">
import type { PropType } from 'vue';
import { computed, ref } from 'vue';
import type { FormKitFrameworkContext } from '@formkit/core';

const props = defineProps({
  context: {
    type: Object as PropType<FormKitFrameworkContext & { disabled: boolean }>,
    required: true,
  },
});
const inputRef = ref<HTMLInputElement | null>(null);

const value = computed({
  get() {
    return props.context._value;
  },
  set(newValue) {
    const allowsDecimals = Number(props.context.attrs?.step ?? 1) < 1;
    const parsed = allowsDecimals
      ? parseFloat(newValue || 0)
      : parseInt(newValue || 0);

    props.context.node.input(Math.max(0, parsed));
  },
});

function onStep(command: 'increment' | 'decrement') {
  if (!inputRef.value) return;
  const input = inputRef.value;

  if (command === 'increment') {
    input.stepUp();
  }
  if (command === 'decrement') {
    input.stepDown();
  }

  // tell formulate value has changed
  input.dispatchEvent(new Event('input'));
}
</script>
<style module>
.wrapper {
  display: flex;
  gap: var(--qtyInputWrapper, 0.5rem);
}

:global(.formkit-inner):has(.wrapper) {
  --fk-border-box-shadow: none;
}

:global(.formkit-outer.qty-row .formkit-wrapper):has(.wrapper) {
  --fk-padding-label: 0;
  --qtyInputWrapper: 0.25rem;

  display: flex;
  align-items: center;
  gap: var(--qtyInputWrapper);
}

.stepper {
  --qtyStepperSize: var(--fk-stepper-size, 2rem);

  width: var(--qtyStepperSize);
  height: var(--qtyStepperSize);
  border: 1px solid var(--neutral-400);
  border-radius: 5em;
}

.input {
  appearance: textfield;
  width: 4em;
  border: 1px solid var(--neutral-400);
  border-radius: 1em;
  text-align: center;
  padding: 0;
}

.input::-webkit-inner-spin-button,
.input::-webkit-outer-spin-button {
  appearance: none;
}

.wrapper button:disabled {
  cursor: not-allowed;
  opacity: 0.7;
}
</style>
